/**
 * Deep Linking Utilities
 * 
 * Handles deep linking to the mobile app and platform detection
 */

import { MOBILE_APP_CONFIG, USER_AGENT_PATTERNS } from '@/config/constants';

export type Platform = 'ios' | 'android' | 'desktop';

/**
 * Detect the user's platform
 */
export const detectPlatform = (): Platform => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (USER_AGENT_PATTERNS.IOS_DEVICES.test(userAgent)) {
    return 'ios';
  }
  
  if (USER_AGENT_PATTERNS.ANDROID_DEVICES.test(userAgent)) {
    return 'android';
  }
  
  return 'desktop';
};

/**
 * Check if the current device is mobile
 */
export const isMobileDevice = (): boolean => {
  return USER_AGENT_PATTERNS.MOBILE_DEVICES.test(navigator.userAgent);
};

/**
 * Attempt to open the mobile app via deep link
 * Returns a promise that resolves with whether the app was opened
 */
export const attemptDeepLink = (path: string, params?: URLSearchParams): Promise<boolean> => {
  return new Promise((resolve) => {
    // Construct the deep link URL
    const deepLinkUrl = constructDeepLink(path, params);
    
    // Track if we're still on the page after attempting to open
    let appOpened = false;
    const startTime = Date.now();
    
    // Create an invisible iframe to trigger the deep link
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = deepLinkUrl;
    document.body.appendChild(iframe);
    
    // Clean up the iframe
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 100);
    
    // Check if the user left the page (app opened)
    const checkIfAppOpened = () => {
      const timeElapsed = Date.now() - startTime;
      
      // If the page was hidden (app opened) or significant time passed, assume app opened
      if (document.hidden || timeElapsed > 2000) {
        appOpened = true;
      }
    };
    
    // Listen for visibility changes
    const visibilityHandler = () => {
      if (document.hidden) {
        appOpened = true;
      }
    };
    
    document.addEventListener('visibilitychange', visibilityHandler);
    
    // Check periodically
    const checkInterval = setInterval(checkIfAppOpened, 200);
    
    // After 2.5 seconds, resolve with the result
    setTimeout(() => {
      clearInterval(checkInterval);
      document.removeEventListener('visibilitychange', visibilityHandler);
      resolve(appOpened);
    }, 2500);
  });
};

/**
 * Construct a deep link URL with the app scheme
 */
export const constructDeepLink = (path: string, params?: URLSearchParams): string => {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  // Get the base scheme (e.g., "auqlistaging://app.staging.auqli.live")
  const scheme = MOBILE_APP_CONFIG.SCHEME;
  
  // Construct the full URL
  let deepLinkUrl = `${scheme}/${cleanPath}`;
  
  // Add query parameters if provided
  if (params && params.toString()) {
    deepLinkUrl += `?${params.toString()}`;
  }
  
  return deepLinkUrl;
};

/**
 * Get the appropriate app store link for the current platform
 */
export const getAppStoreLink = (platform?: Platform): string => {
  const detectedPlatform = platform || detectPlatform();
  
  if (detectedPlatform === 'ios') {
    return MOBILE_APP_CONFIG.APP_STORE.IOS;
  }
  
  if (detectedPlatform === 'android') {
    return MOBILE_APP_CONFIG.APP_STORE.ANDROID;
  }
  
  // Default to Android for desktop
  return MOBILE_APP_CONFIG.APP_STORE.ANDROID;
};

/**
 * Check if the current URL is a share link
 */
export const isShareLink = (searchParams?: URLSearchParams): boolean => {
  if (!searchParams) {
    searchParams = new URLSearchParams(window.location.search);
  }
  
  return searchParams.get('itmSource') === 'share';
};

/**
 * Handle deep linking with fallback to app store
 * This is the main function to use for deep linking
 */
export const handleDeepLinkWithFallback = async (
  path: string,
  params?: URLSearchParams,
  onAppNotInstalled?: () => void
): Promise<void> => {
  // Only attempt deep linking on mobile devices
  if (!isMobileDevice()) {
    onAppNotInstalled?.();
    return;
  }
  
  // Attempt to open the app
  const appOpened = await attemptDeepLink(path, params);
  
  // If app didn't open, call the fallback
  if (!appOpened && onAppNotInstalled) {
    onAppNotInstalled();
  }
};
